import * as _ from "lodash";
import * as angular from "angular";
import { AppTypeDataProvider } from "../../data-provider/app-type.dp";
import { Campaign } from "../../models/campaign.model";
import { Division } from "../../models/division.model";
import { Filter } from "../../models/filter.model";
import { User } from "../../models/user.model";
import { OrganisationUserDataProvider } from "../../data-provider/organisation-user.dp";
import { PermissionCheck } from "../../shared/permission";
import { UserDataProvider } from "../../data-provider/user.dp";
import { DialogUtils } from "../../helper/dialog.helper";
import { ScreenUtils } from "../../helper/screen.helper";
import { LeaderBoardDataProvider } from "../../data-provider/leader-board.dp";
import { Utils } from "../../helper/utils.helper";
import { UserPermission } from "../../utils/user-permissions";

let LeaderBoardComponent = {
    selector: "vcLeaderBoard",
    templateUrl: "/angular/components/leader-board/leader-board.component.html",
    bindings: {},
    controller: class LeaderBoardController {
        public readonly isLeaderView: boolean;
        public readonly description =
            "Identify your top field organizers at a glance";
        public readonly supportUrl: string;
        public readonly emptyPermission = {
            permissionid: null,
            permissionname: "All Permissions",
        };

        public rank = 0;
        public user: User;
        public users: User[] = [];
        public divisions: Division[] = [];
        public loadingStatus = {
            stats: false,
            content: true,
            details: false,
        };
        public customTemplates: any[];
        public emptyDivision: Division;
        public emptyCampaign: Campaign;
        public campaigns: Campaign[] = [];
        public filters: Filter;
        public permissions: any[] = [];
        public headlineStats: any;

        private readonly $scope: any;
        private readonly modelsUtils: any;
        private readonly utils: Utils;
        private readonly dialog: DialogUtils;
        public readonly __env: any;
        public readonly screen: ScreenUtils;

        private dataProvider: LeaderBoardDataProvider;
        private organisationUserDataProvider: OrganisationUserDataProvider;
        private appTypeDataProvider: AppTypeDataProvider;
        public permissionProvider: PermissionCheck;

        constructor(
            $scope: any,
            __env: any,
            ModelsUtils: any,
            LeaderBoardDataProvider: LeaderBoardDataProvider,
            OrganisationUserDataProvider: OrganisationUserDataProvider,
            PermissionCheck: PermissionCheck,
            AppTypeDataProvider: AppTypeDataProvider,
            ScreenUtils: ScreenUtils,
            Utils: Utils,
            DialogUtils: DialogUtils,
            __app_type: string,
            UserDataProvider: UserDataProvider
        ) {
            Utils.accessCheck();
            Utils.runWootric();

            this.$scope = $scope;
            this.__env = __env;
            this.modelsUtils = ModelsUtils;
            this.screen = ScreenUtils;
            this.dialog = DialogUtils;
            this.utils = Utils;

            this.dataProvider = LeaderBoardDataProvider;
            this.organisationUserDataProvider = OrganisationUserDataProvider;
            this.permissionProvider = PermissionCheck;
            this.appTypeDataProvider = AppTypeDataProvider;

            this.isLeaderView = __app_type === "LEADER";
            this.supportUrl =
                "https://support." +
                this.__env.product +
                ".com/your-dashboard/teams-and-analytics/understanding-your-talking-points-page";
            this.user = UserDataProvider.getActiveUser();
            this.customTemplates = this.dataProvider.getCustomDateTemplates();
            this.emptyDivision = new Division({ name: "All Divisions" });
            this.emptyCampaign = this.modelsUtils.campaign({
                campaignname: "All Accounts",
            });
            this.campaigns = [this.emptyCampaign];

            this.appTypeDataProvider.setAppType("all");
            this.filters = new Filter({
                appType: this.appTypeDataProvider.getAppType(),
                limit: 10,
                division: this.emptyDivision,
                permission: this.emptyPermission,
                campaign: this.emptyCampaign,
                dateRange: {
                    selectedTemplate: "Last 30 Days",
                    selectedTemplateName: "Last 30 Days",
                    dateStart: new Date(
                        new Date().getTime() - 30 * 24 * 60 * 60 * 1000
                    ),
                    dateEnd: new Date(),
                },
                order: "attempts",
                direction: "desc",
            });

            this.permissions = [
                this.emptyPermission,
                ...this.permissionProvider.levels.filter(
                    (permission: any) =>
                        permission.permissionid != UserPermission.REVOKED &&
                        permission.permissionid != UserPermission.ACCOUNT_OWNER
                ),
            ];

            this.dataProvider.getDivisions().then((divisions: Division[]) => {
                this.divisions = [this.emptyDivision, ...divisions];
            });

            this.fetchStats();
            this.fetchData();
        }

        $onInit() {
            this.$scope.$watch(
                () => this.filters,
                (newValue: object, oldValue: object) => {
                    if (
                        oldValue &&
                        newValue !== oldValue &&
                        !this.loadingStatus.content
                    ) {
                        this.fetchData();
                    }
                },
                true
            );

            this.$scope.$watch(
                () => this.filters.appType,
                (newValue: string, oldValue: string) => {
                    if (oldValue && newValue !== oldValue) {
                        this.appTypeDataProvider.setAppType(newValue);
                    }
                }
            );

            this.$scope.$watch(
                () => this.filters.division,
                (newValue: Division, oldValue: Division) => {
                    if (newValue.id !== oldValue.id) {
                        let newCampaigns = [
                            this.emptyCampaign,
                            ...newValue.campaigns.map((campaign: Campaign) =>
                                this.modelsUtils.campaign(campaign)
                            ),
                        ];
                        this.campaigns = newCampaigns;
                        this.filters.campaign = this.campaigns[0];
                    }
                }
            );
        }

        refreshPermissionsCount(): void {
            angular.forEach(this.permissions, (permission: any) => {
                switch (permission.permissionid) {
                    case "3":
                        permission.count = this.headlineStats.AccountManager;
                        break;
                    case "4":
                        permission.count = this.headlineStats.Staffer;
                        break;
                    case "5":
                        permission.count = this.headlineStats.LeadOrganizer;
                        break;
                    case "6":
                        permission.count = this.headlineStats.Organizer;
                        break;
                    case "8":
                        permission.count = this.headlineStats.LeaderOwner;
                        break;
                    case "9":
                        permission.count = this.headlineStats.LeaderManager;
                        break;
                    default:
                        permission.count = this.headlineStats.total;
                }
            });
        }

        checkLoading(loading: string, status: string): void {
            if (loading == "content") {
                this.loadingStatus.content = status == "start" ? true : false;
            } else {
                this.loadingStatus.stats = status == "start" ? true : false;
            }
        }

        fetchStats(): void {
            this.organisationUserDataProvider
                .headlineStats()
                .then((stats: object[]) => {
                    this.headlineStats = stats;
                    this.refreshPermissionsCount();
                });
        }

        fetchData(): void {
            let filtersCopy = _.cloneDeep(this.filters);

            if (filtersCopy.division.id == this.emptyDivision.id) {
                delete filtersCopy.division;
            } else {
                filtersCopy.division = filtersCopy.division.id;
            }

            if (
                filtersCopy.permission.permissionid ==
                this.emptyPermission.permissionid
            ) {
                delete filtersCopy.permission;
            } else {
                filtersCopy.permission = filtersCopy.permission.permissionid;
            }

            this.loadingStatus.details = true;
            this.loadingStatus.stats = true;
            this.loadingStatus.content = true;

            this.dataProvider.getRank(filtersCopy).then((rank: number) => {
                this.rank = rank;

                this.dataProvider
                    .getResults(filtersCopy)
                    .then((users: User[]) => {
                        this.users = users.map((user: User, i: number) => {
                            user.rank =
                                filtersCopy.direction == "desc"
                                    ? i + 1
                                    : this.rank - i;
                            return user;
                        });

                        this.loadingStatus.details = false;
                        this.loadingStatus.stats = false;
                        this.loadingStatus.content = false;
                    });
            });
        }

        openViewUserDialog(user: User, $event: any): void {
            this.organisationUserDataProvider
                .getUserCampaigns(user.uid)
                .then((data: any[]) => {
                    user.campaigns = data;

                    this.dialog.show({
                        component: "vc-view-organisation-user",
                        targetEvent: $event,
                        size: "large",
                        scope: this.$scope,
                        locals: {
                            selectedUser: user,
                            selectedUserId: null,
                            editable: true,
                            onUserUpdated: this.utils.bindFunction(() => {
                                this.fetchStats();
                                this.fetchData();
                            }, this),
                            onUserDeleted: this.utils.bindFunction(() => {
                                this.fetchStats();
                                this.fetchData();
                            }, this),
                        },
                    });
                });
        }

        changeOrder(column: string): void {
            if (column == this.filters.order) {
                this.filters.direction =
                    this.filters.direction == "asc" ? "desc" : "asc";
            } else {
                this.filters.order = column;
                this.filters.direction = "desc";
            }
        }
    },
};

angular
    .module("leaderBoard", [])
    .component(LeaderBoardComponent.selector, LeaderBoardComponent);
